/**
 * NetSnapshot — the per-player `snap` / `full` payload. It is the sim's `Snapshot`
 * (docs §3) plus the sections the filter needs that §3 does not list (see README
 * "Deviations"): live-hex flips, focus, ghosts, ally prompts and, on `full`, last-seen ticks.
 */
import type { AllyRequest, FrontReadout, Ghost, HexIdx, Snapshot, SnapshotHeader } from '@sa/sim';

export interface NetHeader extends SnapshotHeader {
  /** own focus hex, −1 if none */
  focus: HexIdx | -1;
  /** own focused-front readout (wire-quantised: ratio in 0.1 steps, rate in whole hexes / min) */
  front: FrontReadout;
}

export interface NetSnapshot extends Snapshot {
  hdr: NetHeader;
  /**
   * Hexes whose live flag flipped since the previous snapshot, ascending.
   * On `full` the receiver starts from "nothing live", so this lists every live hex.
   */
  live: HexIdx[];
  /** Ghost records to add: leader-beacon cities; on `full`, the player's whole ghost memory. */
  ghosts: Ghost[];
  /** Alliance requests sent to or by this player (new ones; on `full`, all pending). */
  allyPrompts: AllyRequest[];
  /**
   * `full` only: per land hex the tick it was last live (−1 never), rounded down to whole
   * seconds. On `full`, `owners` covers every hex and `popRad` every hex (0/0 if never seen).
   */
  lastSeen?: Int32Array;
}

export function emptySnapshot(full: boolean, hdr: NetHeader): NetSnapshot {
  return {
    full,
    hdr,
    owners: [],
    popRad: [],
    upserts: [],
    removes: [],
    tracks: [],
    hints: [],
    detonations: [],
    intercepts: [],
    shares: [],
    phase: [],
    live: [],
    ghosts: [],
    allyPrompts: [],
  };
}
